import { Close as CloseIcon, KeyboardArrowLeft as BackIcon } from "@mui/icons-material";
import { Box, Button, Typography, IconButton } from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { DirectAudioRecorder } from "components/Audio/DirectAudioRecorder";
import { VoiceMemoList } from "./VoiceMemoList";
import { loadVoiceMemoBlob } from "lib/redux/linkedin/voice-memos/slice";

interface VoiceMemo {
  id: string;
  name: string;
  blob?: Blob;
  duration: number;
  url: string;
}

type VoiceMemoRecorderProps = {
  onAccept?: (blob: Blob, duration: number) => void
  onCancel?: () => void
  initialBlob?: Blob | null
  recentVoiceMemos: VoiceMemo[]
}

type Mode = 'choose' | 'record' | 'select';

/**
 * Voice memo recorder widget.
 *
 * @param onAccept - called with Blob and duration when the user hits the ok button.
 *                   For best render efficiency, pass in something defined with useCallback.
 * @param onCancel - callback for when the user hits the close button. Define with useCallback for best efficiency.
 * @param initialBlob - an audio file to prefill the widget with. Resets the recorder state when it changes.
 * @param recentVoiceMemos - list of recent voice memos to show in the selection view
 */
export const VoiceMemoRecorder: React.FC<VoiceMemoRecorderProps> = React.memo(({
  onAccept,
  onCancel,
  initialBlob = null,
  recentVoiceMemos
}) => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState<Mode>('choose');
  
  // Get voice notes from Redux store
  const voiceNotesInRedux = useSelector((state: RootState) => state.linkedinVoiceNotes.vms || []);
  
  const handleSelectMemo = (memo: VoiceMemo) => {
    // If memo has blob already, use it
    if (memo.blob) {
      onAccept?.(memo.blob, memo.duration);
      return;
    }
    
    // Otherwise find it in Redux store
    const reduxMemo = voiceNotesInRedux.find(vm => vm.linkedin_voice_memo_id === memo.id);
    if (reduxMemo?.blob) {
      onAccept?.(reduxMemo.blob, memo.duration);
    }
  };

  return (
    <Box sx={{
      width: 320,
      backgroundColor: 'background.paper',
      borderRadius: 1,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      border: '1px solid rgb(229, 231, 235)',
      overflow: 'hidden'
    }}>
      {mode === 'choose' && (
        <>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid rgb(229, 231, 235)', 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
            position: 'relative'
          }}>
            <Typography sx={{ 
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'rgb(31, 41, 55)',
              flex: 1,
              textAlign: 'center'
            }}>
              Voice Note
            </Typography>
            {onCancel && (
      <IconButton
        onClick={onCancel}
                size="small"
        sx={{
                  position: 'absolute', 
                  right: 8, 
                  color: 'rgb(107, 114, 128)',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
      </IconButton>
            )}
  </Box> 
          
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ 
              fontSize: '0.875rem',
              fontWeight: 400,
              color: 'rgb(75, 85, 99)',
              textAlign: 'center'
            }}>
              What would you like to do?
            </Typography>
            
            <Button
              fullWidth
              variant="contained"
              onClick={() => setMode('record')}
              sx={{
                bgcolor: '#0a66c2',
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#004182',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              Record New
            </Button>
            
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setMode('select')}
              disabled={recentVoiceMemos.length === 0}
              sx={{
                borderColor: '#0a66c2',
                color: '#0a66c2',
                textTransform: 'none',
                fontWeight: 500,
                py: 1,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#004182',
                  bgcolor: 'rgba(10, 102, 194, 0.04)',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                },
                '&.Mui-disabled': {
                  borderColor: 'rgb(229, 231, 235)',
                  color: 'rgb(156, 163, 175)'
                }
              }}
            >
              Select Existing
            </Button>
          </Box>
        </>
      )}

      {mode === 'record' && (
        <>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid rgb(229, 231, 235)', 
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}>
      <IconButton
              onClick={() => setMode('choose')}
              size="small"
              sx={{
                color: 'rgb(107, 114, 128)',
                p: 0.5,
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <BackIcon fontSize="small" />
      </IconButton>
            
            <Typography sx={{ 
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'rgb(31, 41, 55)',
              flex: 1,
              textAlign: 'center'
            }}>
              Record Voice Note
            </Typography>
            
            {onCancel && (
      <IconButton
                onClick={onCancel}
                size="small"
                sx={{
                  color: 'rgb(107, 114, 128)',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
      </IconButton>
            )}
    </Box>
          
          <DirectAudioRecorder
            onAccept={onAccept}
            onCancel={() => setMode('choose')}
            initialBlob={initialBlob}
            showUseThisButton={true}
          />
        </>
      )}

      {mode === 'select' && (
        <>
          <Box sx={{ 
            p: 1.5, 
            borderBottom: '1px solid rgb(229, 231, 235)', 
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}>
      <IconButton
              onClick={() => setMode('choose')}
              size="small"
              sx={{
                color: 'rgb(107, 114, 128)',
                p: 0.5,
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <BackIcon fontSize="small" />
      </IconButton>
            
            <Typography sx={{ 
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'rgb(31, 41, 55)',
              flex: 1,
              textAlign: 'center'
            }}>
              Select a voice note
            </Typography>
            
            {onCancel && (
      <IconButton
                onClick={onCancel}
                size="small"
                sx={{
                  color: 'rgb(107, 114, 128)',
                  p: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
      </IconButton>
            )}
          </Box>
          
          <Box sx={{ p: 2 }}>
            <VoiceMemoList
              voiceMemos={recentVoiceMemos}
              onSelect={handleSelectMemo}
            />
          </Box>
        </>
      )}
    </Box>
  );
});
